import './MarketplaceBuyers.scss';
import React, { useEffect, useState } from 'react';
import { useStateSelector } from '../../../../store/selectors';
import BootstrapTable from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import filterFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import paginationFactory from '../../../../components/react-bootstrap-table-next/react-bootstrap-table2-paginator';
import { TableFilter } from '../../../../components/Table/models/table-filter';
import { Button } from 'reactstrap';
import { InviteNewBuyerModal } from '../../Modals/InviteNewBuyerModal/InviteNewBuyerModal';
import UserDetailsModal from '../../../Admin/Permissions/Users/Modal/UserDetailsModal';
import { MarketplaceBuyer } from '../../../../slices/marketplace/buyer/models/buyer';
import { useDispatch } from 'react-redux';
import { GetBuyers } from '../../../../slices/marketplace/buyer/thunks/get-buyers.thunk';
import BuyerProfilePreviewPopup from '../../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import { BuyerNdaTermPreferencesPopup } from '../../../../components/BuyerNdaTermPreferencesPopup/BuyerNdaTermPreferencesPopup';

export const MarketplaceBuyers = () => {
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(5);
    const [data, setData] = useState<MarketplaceBuyer[]>([]);
    const [isInviteNewBuyerModalVisible, setIsInviteNewBuyerModalVisible] =
        useState(false);
    const [isUserDetailsModalVisible, setIsUserDetailsModalVisible] =
        useState(false);
    const [isBuyerProfilePopupVisible, setIsBuyerProfilePopupVisible] =
        useState(false);
    const [
        isBuyerNdaTermPreferencesPopupVisible,
        setIsBuyerNdaTermPreferencesPopupVisible,
    ] = useState(false);
    const [selectedMarketplaceBuyer, setSelectedMarketplaceBuyer] =
        useState<MarketplaceBuyer>(null);
    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'userFullName',
            order: 'asc',
        },
    ];
    const [currentSort, setCurrentSort] = useState<TableFilter>(
        new TableFilter({ filters: [], logic: '' }, page, sizePerPage, {
            dir: 'asc',
            field: 'userFullName',
        })
    );
    const dispatch = useDispatch() as any;
    const buyersTable = useStateSelector(
        (state) => state.buyersState?.buyersTable
    );

    useEffect(() => {
        fetchData(currentSort);
    }, []);

    const fetchData = async (postObj: TableFilter) => {
        await dispatch(GetBuyers(postObj));
    };

    useEffect(() => {
        setData(buyersTable.buyers);
        setPage(buyersTable.pageNumber);
        setTotalSize(buyersTable.totalItems);
        setSizePerPage(buyersTable.pageSize);
    }, [buyersTable]);

    const handleTableChange = (_type: any, newState: any) => {
        const { page, sizePerPage, sortField, sortOrder } = newState;

        const updatedPostObj = new TableFilter(
            { filters: [], logic: '' },
            page,
            sizePerPage,
            { dir: sortOrder, field: sortField }
        );
        setCurrentSort(updatedPostObj);
        fetchData(updatedPostObj);
    };

    const showUserDetailPopup = (buyer: MarketplaceBuyer) => {
        setSelectedMarketplaceBuyer(buyer);
        setIsUserDetailsModalVisible(true);
    };

    const openBuyerProfile = (buyer: MarketplaceBuyer) => {
        setSelectedMarketplaceBuyer(buyer);
        setIsBuyerProfilePopupVisible(true);
    };

    const openNdaTermPreferencesPopup = (buyer: MarketplaceBuyer) => {
        setSelectedMarketplaceBuyer(buyer);
        setIsBuyerNdaTermPreferencesPopupVisible(true);
    };

    const closeNdaTermPreferencesPopup = () => {
        setIsBuyerNdaTermPreferencesPopupVisible(false);
        setSelectedMarketplaceBuyer(null);
    };

    const closeBuyerProfile = () => {
        setIsBuyerProfilePopupVisible(false);
        setSelectedMarketplaceBuyer(null);
    };

    const columns = [
        {
            dataField: 'userFullName',
            text: 'Buyer Name',
            headerClasses: 'buyer-name',
            sort: true,
            formatter: (cellContent: string, row: MarketplaceBuyer) => (
                <div
                    className={'link'}
                    onClick={() => {
                        showUserDetailPopup(row);
                    }}>
                    {cellContent}
                </div>
            ),
        },
        {
            dataField: 'corporateAffiliationName',
            text: 'Corporate Affiliation',
            headerClasses: 'corporate-affiliation-name',
            sort: true,
        },
        {
            dataField: 'activeEngagementsCount',
            headerClasses: 'active-engagements-count',
            text: '# of active engagements',
            sort: true,
        },
        {
            dataField: 'id',
            text: 'Actions',
            headerClasses: 'actions',
            formatter: (_cellContent: number, row: MarketplaceBuyer) => {
                return (
                    <div className="buttons-wrapper">
                        <Button
                            className="btn btn-primary"
                            onClick={() => openBuyerProfile(row)}>
                            History
                        </Button>

                        <Button
                            className="btn btn-primary"
                            onClick={() => openNdaTermPreferencesPopup(row)}>
                            NDA Term Preferences
                        </Button>
                    </div>
                );
            },
        },
    ];

    return (
        <div className="marketplace-buyers-tab">
            <div>
                <div className="mb-3 page-header">
                    <div className="header-title">Buyers</div>
                    <div>
                        <Button
                            type="button"
                            className="btn btn-primary ml-3 back-btn"
                            onClick={() => {
                                setIsInviteNewBuyerModalVisible(true);
                            }}>
                            Invite New Buyer
                        </Button>
                    </div>
                </div>

                <div
                    role="tabpanel"
                    id="panel-0"
                    aria-labelledby="tab-0"
                    aria-hidden="false">
                    <div className="card-body">
                        <BootstrapTable
                            bootstrap4
                            remote
                            keyField="id"
                            data={data}
                            columns={columns}
                            defaultSorted={defaultSorted}
                            filter={filterFactory()}
                            pagination={paginationFactory({
                                page,
                                sizePerPage,
                                totalSize,
                                tooltipTargetId: 'marketplace-buyers-tooltip',
                            })}
                            onTableChange={handleTableChange}
                            noDataIndication="No Buyers available"
                        />
                    </div>
                </div>
                {isInviteNewBuyerModalVisible && (
                    <InviteNewBuyerModal
                        isVisible={isInviteNewBuyerModalVisible}
                        onClose={() => {
                            setIsInviteNewBuyerModalVisible(false);
                        }}
                    />
                )}
                {isUserDetailsModalVisible && (
                    <div className="user-details-popup">
                        <UserDetailsModal
                            onClose={() => {
                                setIsUserDetailsModalVisible(false);
                                fetchData(currentSort);
                            }}
                            isVisisble={isUserDetailsModalVisible}
                            userID={selectedMarketplaceBuyer.userId}
                        />
                    </div>
                )}
            </div>
            {isBuyerProfilePopupVisible ? (
                <BuyerProfilePreviewPopup
                    buyerId={selectedMarketplaceBuyer.id}
                    close={closeBuyerProfile}
                />
            ) : (
                <></>
            )}
            {isBuyerNdaTermPreferencesPopupVisible ? (
                <BuyerNdaTermPreferencesPopup
                    buyerId={selectedMarketplaceBuyer.id}
                    close={closeNdaTermPreferencesPopup}
                />
            ) : (
                <></>
            )}
        </div>
    );
};
